import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v1/colors";
import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { PartWithChildrenDto } from "#modules/parts/infra/fetchPartWithChildren.api";
import { filterPartsByName } from "#modules/similarPart/domain/filterPartsByName";
import { AssemblyTreeHeader } from "#modules/similarPart/view/assembly/AssemblyTreeHeader";
import { PartTree } from "#modules/similarPart/view/assembly/PartTree";
import { Alert } from "#shared/view/components/Alert";
import { Group } from "#shared/view/components/Group";
import { SimpleLoader } from "#shared/view/components/Loader";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { SearchBar } from "#shared/view/designSystem/SearchBar";

import { SourcePartCard } from "../part/SourcePartCard";

type Props = {
  assembly: PartWithChildrenDto;
  part: PartDto;
  setPart: Dispatch<SetStateAction<(PartDto & { cosineSimilarity?: number }) | null>>;
  isAssemblyAnalyzing: boolean;
  refetch?: () => void;
};

export const AssemblyTreeSection = ({
  assembly,
  part,
  setPart,
  isAssemblyAnalyzing,
  refetch,
}: Props) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  const filteredAssembly = useMemo(() => {
    if (!searchTerm.trim()) {
      return assembly;
    }
    return filterPartsByName(assembly, searchTerm);
  }, [assembly, searchTerm]);

  return (
    <Stack
      pr="xs"
      style={{
        borderRightWidth: 1,
        borderColor: colors.gray[2],
        borderRightStyle: "solid",
        overflow: "scroll",
        maxWidth: "30%",
      }}
      gap="xxs"
    >
      <AssemblyTreeHeader />
      <SourcePartCard part={part} featuresDisplayMode="truncate" onSubmitPartDimensions={refetch} />

      {isAssemblyAnalyzing && (
        <Alert p="xs">
          <Group align="center">
            <Text c="dimmed">{"L'assemblage est en cours d'analyse"}</Text>
            <SimpleLoader size={"xs"} type="dots" />
          </Group>
        </Alert>
      )}

      <SearchBar
        placeholder={t("similarPart.filter.searchByName")}
        value={searchTerm}
        onChange={setSearchTerm}
        onClear={() => setSearchTerm("")}
      />
      <PartTree part={filteredAssembly} onSelect={setPart} />
    </Stack>
  );
};
